import { useMemo } from "react";
import dayjs from "dayjs";
import { Task } from "@thepocman/gantt-task-react";
import { COLORS, DOT_COLUMN_WIDTH, type TimelineUtils } from "./timelineUtils";
import type { TimelineItem, TimelineProps } from "./timeline";

export type TimelineRow = {
  id: string;
  item: TimelineItem;
  task: Task;
  fillColor: string;
  borderColor: string;
};

type UseTimelineRowsArgs = {
  data: TimelineItem[];
  columns: TimelineProps["columns"];
  utils: TimelineUtils;
  rowHeight: number;
  headerHeight: number;
  maxChartHeight?: number;
};

export function useTimelineRows({
  data,
  columns = [],
  utils,
  rowHeight,
  headerHeight,
  maxChartHeight,
}: UseTimelineRowsArgs) {
  const visibleColumns = useMemo(() => {
    if (!columns.length) {
      return [...utils.defaultVisibleColumns];
    }
    return columns
      .filter((col) => col.visibility === "visible")
      .map((col) => col.fieldName);
  }, [columns, utils]);

  const gridTemplateColumns = useMemo(() => {
    const visibleWidths = visibleColumns
      .map((column) => `${utils.getTableColumnWidth(column)}px`)
      .join(" ");
    return `${DOT_COLUMN_WIDTH}px ${visibleWidths}`;
  }, [visibleColumns, utils]);

  const minTableWidth = useMemo(() => {
    return visibleColumns.reduce(
      (sum, column) => sum + utils.getTableColumnWidth(column),
      DOT_COLUMN_WIDTH,
    );
  }, [visibleColumns, utils]);

  const rows = useMemo(() => {
    return data
      .map<TimelineRow | null>((item, index) => {
        const start = dayjs(item.start_date);
        const end =
          item.ongoing_derived === true ? dayjs() : dayjs(item.end_date);

        if (!start.isValid() || !end.isValid()) return null;

        const safeEnd = end.isAfter(start)
          ? end.toDate()
          : start.add(1, "day").toDate();
        const fillColor =
          utils.getLegendFillColor(item.core_table) || COLORS[0];
        const borderColor =
          utils.getLegendBorderColor(item.core_table) || COLORS[0];
        const id = `${item.core_table}-${item.start_date}-${item.end_date}-${index}`;

        const task: Task = {
          id,
          name: item.event_name.toUpperCase(),
          start: start.toDate(),
          end: safeEnd,
          progress: 100,
          type: "task",
          isDisabled: true,
          isRelationDisabled: true,
          styles: {
            barBackgroundColor: fillColor,
            barBackgroundSelectedColor: fillColor,
            barProgressColor: fillColor,
            barProgressSelectedColor: fillColor,
          },
        };

        return { id, item, fillColor, borderColor, task };
      })
      .filter((row): row is TimelineRow => row !== null);
  }, [data, utils]);

  const tasks = useMemo(() => rows.map((row) => row.task), [rows]);

  const contentBasedRowsHeight = useMemo(
    () => rows.length * rowHeight,
    [rows.length, rowHeight],
  );

  const contentBasedChartHeight = useMemo(
    () => contentBasedRowsHeight + headerHeight,
    [contentBasedRowsHeight, headerHeight],
  );

  const chartHeight = useMemo(() => {
    if (!maxChartHeight) return contentBasedChartHeight;

    const maxRowsHeight = maxChartHeight - headerHeight;
    const visibleRows = Math.floor(maxRowsHeight / rowHeight);
    const adjustedRowsHeight = visibleRows * rowHeight;

    return Math.min(contentBasedChartHeight, adjustedRowsHeight + headerHeight);
  }, [contentBasedChartHeight, maxChartHeight, headerHeight, rowHeight]);

  const rowsHeight = useMemo(
    () => chartHeight - headerHeight,
    [chartHeight, headerHeight],
  );

  const minimumRowDisplayed = useMemo(() => {
    const visibleRows = Math.floor(rowsHeight / rowHeight);
    return Math.min(visibleRows, rows.length);
  }, [rowsHeight, rowHeight, rows.length]);

  const itemByTaskId = useMemo(() => {
    const map = new Map<string, TimelineItem>();
    rows.forEach((row) => {
      map.set(row.id, row.item);
    });
    return map;
  }, [rows]);

  return {
    visibleColumns,
    gridTemplateColumns,
    minTableWidth,
    rows,
    tasks,
    rowsHeight,
    chartHeight,
    minimumRowDisplayed,
    itemByTaskId,
  };
}
